import React, { useState, useEffect, useRef } from 'react';
import ObjectionPanel from './ObjectionPanel';
import { timeLogService } from '../services/timeLogService';
import { structureCallNotes } from '../services/aiNotesService';
import { downloadIcsEvent, buildGoogleCalendarUrl, parseAppointmentStart } from '../utils/calendarIcs';
import { CALL_OUTCOMES } from '../constants/callOutcomes';
import { useCallOutcomeFromNotes } from '../hooks/useCallOutcomeFromNotes';
import { CallOutcomeThinking, CallOutcomeAmbiguousHint } from './CallOutcomeThinking';
import { layoutCallAndObjectionPanels, CALL_MODAL_WIDTH } from '../utils/callPanelLayout';
import { callScriptService } from '../services/callScriptService';

const OUTCOMES = CALL_OUTCOMES;

const FOLLOWUP_OFFSETS = [
  { label: 'Heute',      days: 0 },
  { label: 'Morgen',     days: 1 },
  { label: '3 Tage',     days: 3 },
  { label: '1 Woche',    days: 7 },
  { label: '2 Wochen',   days: 14 },
];

const FOLLOWUP_TYPES = ['Anruf', 'E-Mail', 'Besuch'];

function addDays(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}
function nowDate() { return new Date().toISOString().slice(0, 10); }
function nowTime() { return new Date().toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }); }

// Convert "DD.MM.YYYY" → "YYYY-MM-DD" for <input type="date">
function deToISO(deDate) {
  if (!deDate) return nowDate();
  const parts = deDate.split('.');
  if (parts.length === 3) return `${parts[2]}-${parts[1]}-${parts[0]}`;
  return nowDate();
}

export default function FloatingCall({ lead, activity, currentUser, onSave, onSaveTask, onClose }) {
  const isEdit = !!activity;
  const [date, setDate]           = useState(isEdit ? deToISO(activity.date) : nowDate());
  const [time, setTime]           = useState(isEdit ? (activity.time || nowTime()) : nowTime());
  const [duration, setDuration]   = useState(isEdit ? (activity.duration || '') : '');
  const [outcome, setOutcome]     = useState(isEdit ? (activity.outcome || 'reached_followup') : 'reached_followup');
  const [notes, setNotes]         = useState(isEdit ? (activity.note || '') : '');
  const [createTask, setCreateTask] = useState(false);
  const [taskType, setTaskType]   = useState('Anruf');
  const [taskOffset, setTaskOffset] = useState(3);
  const [taskNote, setTaskNote]   = useState('');
  const pairedLayout = React.useMemo(() => layoutCallAndObjectionPanels(), []);
  const [pos, setPos]             = useState(() => ({ x: pairedLayout.call.x, y: pairedLayout.call.y }));
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError]     = useState('');
  const [calendarOffer, setCalendarOffer] = useState(false);
  const [userPickedOutcome, setUserPickedOutcome] = useState(isEdit);
  const [showScript, setShowScript] = useState(false);
  const [scriptText, setScriptText] = useState(() => callScriptService.getForBranch(lead?.branch)?.text || '');
  const [editScript, setEditScript] = useState(false);
  const [callQuality, setCallQuality] = useState(isEdit ? (activity?.callQuality || 0) : 0);

  const {
    inferring: outcomeInferring,
    inferLabel: outcomeInferLabel,
    showAmbiguous,
    highlightSlug,
    onNotesBlur,
    dismissAmbiguous,
  } = useCallOutcomeFromNotes({
    currentOutcome: outcome,
    setOutcome,
    leadName: lead?.name,
    company: lead?.company,
    skipIfOutcomeChosen: userPickedOutcome,
  });

  const dragging    = useRef(false);
  const dragOff     = useRef({ x: 0, y: 0 });
  const startTimeRef = useRef(isEdit ? null : Date.now());
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (isEdit) return;
    const id = setInterval(() => setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000)), 1000);
    return () => clearInterval(id);
  }, [isEdit]);

  function fmtElapsed(s) {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${String(sec).padStart(2, '0')}`;
  }

  // Auto-suggest follow-up based on outcome (only for new entries)
  useEffect(() => {
    if (isEdit) return;
    const needsFollowup = ['reached_followup', 'not_voicemail', 'not_busy', 'not_noanswer', 'callback'];
    setCreateTask(needsFollowup.includes(outcome));
  }, [outcome]);

  useEffect(() => {
    function onMove(e) { if (!dragging.current) return; setPos({ x: e.clientX - dragOff.current.x, y: e.clientY - dragOff.current.y }); }
    function onUp()   { dragging.current = false; }
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, []);

  function startDrag(e) {
    if (e.target.closest('button, input, select, textarea')) return;
    dragging.current = true;
    dragOff.current = { x: e.clientX - pos.x, y: e.clientY - pos.y };
    e.preventDefault();
  }

  function handleSave() {
    const parts    = date.split('-');
    const dateStr  = `${parts[2]}.${parts[1]}.${parts[0]}`;
    const monthStr = new Date(date).toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
    const meta     = OUTCOMES.find(o => o.id === outcome);

    const autoElapsed = !isEdit && elapsed > 0 ? fmtElapsed(elapsed) : null;
    const finalDuration = duration.trim() || autoElapsed || null;

    onSave({
      // Preserve existing id when editing so LeadPanel can update in-place
      id: isEdit ? activity.id : Date.now(),
      type: 'call',
      outcomeSaved: true,
      month: monthStr,
      title: `Ausgehender Anruf – ${meta?.label || outcome}`,
      by: activity?.by || currentUser?.name || currentUser?.email || 'Unbekannt',
      byUserId: activity?.byUserId || currentUser?.id || null,
      byEmail: activity?.byEmail || currentUser?.email || null,
      date: dateStr,
      time,
      note: notes.trim() || null,
      duration: finalDuration,
      outcome,
      outcomeLabel: meta?.label || outcome,
      callQuality: callQuality || null,
      done: false,
      ts: new Date().toISOString(),
    });

    if (!isEdit && currentUser && lead?.id) {
      const rawDur = duration.trim();
      let minutes = 0;
      if (rawDur) {
        const parts = rawDur.split(':');
        minutes = parts.length === 2 ? parseInt(parts[0]) * 1 + Math.round(parseInt(parts[1]) / 60) : parseInt(parts[0]) || 0;
        if (parts.length === 2) minutes = parseInt(parts[0]) + (parseInt(parts[1]) >= 30 ? 1 : 0);
      } else {
        minutes = Math.max(1, Math.round(elapsed / 60));
      }
      if (minutes > 0) {
        timeLogService.add(currentUser.id, {
          leadId: lead.id,
          minutes,
          note: `Anruf – ${OUTCOMES.find(o => o.id === outcome)?.label || outcome}`,
        });
      }
    }

    if (createTask && onSaveTask) {
      const dueDate = addDays(taskOffset);
      const dueParts = dueDate.split('-');
      onSaveTask({
        id: Date.now() + 1,
        title: `${taskType}: ${lead.name}`,
        type: taskType,
        priority: 'Mittel',
        dueDate,
        dueTime: '09:00',
        dueDatetime: `${dueDate}T09:00`,
        dueDateDisplay: `${dueParts[2]}.${dueParts[1]}.${dueParts[0]}`,
        notes: taskNote.trim() || `Follow-up nach Call (${meta?.label || outcome})`,
        lead: { id: lead.id, name: lead.name, company: lead.company },
        assignedTo: currentUser?.name || 'Robin Obrecht',
        done: false,
      });
    }

    if (!isEdit && outcome === 'reached_appt') {
      setCalendarOffer(true);
      return;
    }
    onClose();
  }

  function calendarOpts() {
    const start = parseAppointmentStart({ dateIso: date, time });
    const title = `Termin: ${lead?.name || 'Lead'}${lead?.company && lead.company !== '–' ? ` (${lead.company})` : ''}`;
    const description = [notes.trim(), lead?.tel && lead.tel !== '–' ? `Tel: ${lead.tel}` : ''].filter(Boolean).join('\n');
    return { title, description, location: lead?.city && lead.city !== '–' ? lead.city : '', start, leadName: lead?.name };
  }

  const selectedOutcome = OUTCOMES.find(o => o.id === outcome);

  return (
    <>
    <ObjectionPanel
      branch={lead?.branch}
      lead={lead}
      initialX={pairedLayout.objection.x}
      initialY={pairedLayout.objection.y}
      initialW={pairedLayout.objectionSize.w}
      initialH={pairedLayout.objectionSize.h}
      forceInitialPosition
      zIndex={401}
    />
    <div style={{
      position: 'fixed', left: pos.x, top: pos.y, zIndex: 400,
      width: CALL_MODAL_WIDTH,
      background: 'var(--glass-modal)',
      backdropFilter: 'var(--glass-blur-lg)',
      WebkitBackdropFilter: 'var(--glass-blur-lg)',
      borderRadius: 16,
      border: '1px solid rgba(255,255,255,0.80)',
      boxShadow: '0 32px 80px rgba(15,40,100,0.22), 0 8px 24px rgba(15,40,100,0.12), 0 0 0 0.5px rgba(255,255,255,0.9), inset 0 1px 0 rgba(255,255,255,1)',
      overflow: 'hidden', fontFamily: 'var(--font)',
    }}>
      {/* Header */}
      <div onMouseDown={startDrag} style={{ background: 'linear-gradient(135deg, rgba(16,100,60,0.88), rgba(22,163,74,0.76))', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', padding: '11px 14px', cursor: 'grab', userSelect: 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, borderBottom: '1px solid rgba(255,255,255,0.18)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.85)" strokeWidth="2">
            <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 015.19 15a19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
          </svg>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>{isEdit ? 'Anruf bearbeiten' : 'Anruf protokollieren'}</span>
          {!isEdit && (
            <span style={{ fontFamily: 'var(--mono)', fontSize: 12, color: elapsed >= 60 ? '#4ade80' : 'rgba(255,255,255,0.6)', fontWeight: 700, background: 'rgba(255,255,255,0.1)', padding: '2px 7px', borderRadius: 5, marginLeft: 4 }}>
              ⏱ {fmtElapsed(elapsed)}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <button
            type="button"
            onClick={() => setShowScript(v => !v)}
            title="Anruf-Skript anzeigen"
            style={{ background: showScript ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.08)', border: `1px solid ${showScript ? 'rgba(255,255,255,0.4)' : 'rgba(255,255,255,0.15)'}`, cursor: 'pointer', padding: '3px 8px', borderRadius: 5, color: '#fff', fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4, transition: 'all .12s' }}
          >
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
            Skript
          </button>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.7)" strokeWidth="2.2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
      </div>

      {/* Contact chip */}
      <div style={{ padding: '9px 14px', borderBottom: '1px solid rgba(255,255,255,0.35)', display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.12)' }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Kontakt</span>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--accent)', background: 'var(--accent-bg)', border: '1px solid var(--accent-border)', padding: '2px 10px', borderRadius: 20, backdropFilter: 'blur(8px)', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.6)' }}>{lead.name}</span>
        {lead.company && lead.company !== '–' && <span style={{ fontSize: 12, color: 'var(--text2)' }}>{lead.company}</span>}
      </div>

      {/* Script panel */}
      {showScript && (
        <div style={{ borderBottom: '1px solid rgba(255,255,255,0.35)', background: 'rgba(255,255,255,0.20)', maxHeight: 220, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '7px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0, borderBottom: '1px solid rgba(255,255,255,0.30)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
              <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Anruf-Skript</span>
            </div>
            <button
              type="button"
              onClick={() => setEditScript(v => !v)}
              style={{ fontSize: 11, color: '#0f8a53', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, padding: '2px 6px' }}
            >
              {editScript ? '✓ Fertig' : 'Bearbeiten'}
            </button>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: '10px 14px' }}>
            {editScript ? (
              <textarea
                value={scriptText}
                onChange={e => setScriptText(e.target.value)}
                onBlur={() => {
                  const all = callScriptService.getAll();
                  const branch = lead?.branch || '';
                  const existing = all.find(s => s.branch?.toLowerCase() === branch.toLowerCase()) || all[0];
                  if (existing) {
                    const updated = all.map(s => s.id === existing.id ? { ...s, text: scriptText } : s);
                    callScriptService.saveAll(updated);
                  }
                }}
                style={{ width: '100%', minHeight: 100, border: '1.5px solid var(--accent)', borderRadius: 8, padding: '8px 10px', fontSize: 12, fontFamily: 'var(--mono)', resize: 'vertical', outline: 'none', boxSizing: 'border-box', background: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(8px)', color: 'var(--text)', lineHeight: 1.6 }}
              />
            ) : (
              <pre style={{ margin: 0, fontSize: 12, color: 'var(--text)', lineHeight: 1.7, fontFamily: 'var(--font, inherit)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {scriptText.replace(/\{\{firma\}\}/gi, lead?.company && lead.company !== '–' ? lead.company : '{{firma}}').replace(/\{\{name\}\}/gi, lead?.name || '{{name}}').replace(/\{\{vorname\}\}/gi, (lead?.name || '').split(' ')[0] || '{{vorname}}')}
              </pre>
            )}
          </div>
        </div>
      )}

      <div style={{ padding: 14 }}>
        {/* Date / Time / Duration */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 12 }}>
          <div>
            <div style={lbStyle}>Datum</div>
            <input type="date" value={date} onChange={e => setDate(e.target.value)} style={inStyle} />
          </div>
          <div>
            <div style={lbStyle}>Uhrzeit</div>
            <input type="time" value={time} onChange={e => setTime(e.target.value)} style={inStyle} />
          </div>
          <div>
            <div style={lbStyle}>Dauer (optional)</div>
            <input
              type="text"
              value={duration}
              onChange={e => setDuration(e.target.value)}
              placeholder={!isEdit && elapsed > 0 ? `Auto: ${fmtElapsed(elapsed)}` : 'z.B. 3:42'}
              title={!isEdit ? `Timer läuft: ${fmtElapsed(elapsed)} — wird beim Speichern automatisch eingetragen` : ''}
              style={{ ...inStyle, color: duration ? '#17382a' : '#93a89a' }}
            />
          </div>
        </div>


        {/* Notes */}
        <div style={{ marginBottom: 12 }}>
          <div style={{ ...lbStyle, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>Notizen (optional)</span>
            <button
              type="button"
              disabled={aiLoading || !notes.trim()}
              onClick={async () => {
                if (!notes.trim()) return;
                setAiLoading(true);
                setAiError('');
                try {
                  const meta = OUTCOMES.find(o => o.id === outcome);
                  const result = await structureCallNotes({
                    notes,
                    leadName: lead?.name,
                    company: lead?.company,
                    outcome: meta?.label,
                  });
                  let newNote = result.note || notes;
                  if (result.nextStep) newNote += '\n→ ' + result.nextStep;
                  setNotes(newNote);
                } catch (err) {
                  setAiError(err.message || 'KI-Fehler');
                  setTimeout(() => setAiError(''), 4000);
                } finally {
                  setAiLoading(false);
                }
              }}
              title="KI-Notizen strukturieren"
              style={{
                display: 'flex', alignItems: 'center', gap: 4,
                height: 22, padding: '0 8px', borderRadius: 5,
                background: aiLoading ? '#e2ebe3' : 'rgba(29,190,116,0.08)',
                border: '1px solid rgba(29,190,116,0.25)',
                color: aiLoading ? '#93a89a' : '#0f8a53',
                fontSize: 11, fontWeight: 600, cursor: aiLoading || !notes.trim() ? 'not-allowed' : 'pointer',
                opacity: !notes.trim() ? 0.5 : 1,
              }}
            >
              {aiLoading ? (
                <span style={{ display: 'inline-block', width: 10, height: 10, border: '2px solid #159c5f', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
              ) : '✨'}
              {' '}KI
            </button>
          </div>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Was wurde besprochen? Feld verlassen → Ergebnis-Vorschlag" rows={2}
            style={{ ...inStyle, height: 'auto', padding: '8px 10px', resize: 'vertical', lineHeight: 1.65, width: '100%', boxSizing: 'border-box' }}
            onFocus={e => e.target.style.borderColor = '#159c5f'}
            onBlur={e => { e.target.style.borderColor = '#c8d6cb'; if (!isEdit) onNotesBlur(notes); }}
          />
          {aiError && (
            <div style={{ fontSize: 11, color: '#dc2626', marginTop: 4, padding: '4px 8px', background: 'rgba(220,38,38,0.07)', borderRadius: 5, border: '1px solid rgba(220,38,38,0.2)' }}>
              {aiError}
            </div>
          )}
        </div>

        {outcomeInferring && <CallOutcomeThinking label={outcomeInferLabel} />}

        <div style={{ marginBottom: 12 }}>
          <div style={lbStyle}>Ergebnis</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
            {OUTCOMES.map(o => {
              const active = outcome === o.id;
              const flash = highlightSlug === o.id;
              return (
                <button key={o.id} type="button" onClick={() => { setUserPickedOutcome(true); setOutcome(o.id); }}
                  style={{
                    height: 34, borderRadius: 7, border: `1.5px solid ${active || flash ? o.color : '#c8d6cb'}`,
                    background: active || flash ? `${o.color}18` : 'rgba(255,255,255,0.45)',
                    backdropFilter: 'blur(8px)',
                    WebkitBackdropFilter: 'blur(8px)',
                    color: active || flash ? o.color : 'var(--text2)',
                    fontSize: 12, fontWeight: active || flash ? 600 : 400,
                    cursor: 'pointer', textAlign: 'left', padding: '0 10px',
                    display: 'flex', alignItems: 'center', gap: 6, transition: 'all .2s',
                    boxShadow: flash && !active ? `0 0 0 2px ${o.color}35` : 'none',
                  }}>
                  <span style={{ fontSize: 13 }}>{o.emoji}</span> {o.label}
                </button>
              );
            })}
          </div>
        </div>

        {showAmbiguous && <CallOutcomeAmbiguousHint onDismiss={dismissAmbiguous} />}

        {/* Call quality rating */}
        <div style={{ marginBottom: 12 }}>
          <div style={lbStyle}>Gesprächsqualität</div>
          <div style={{ display: 'flex', gap: 4 }}>
            {[1, 2, 3, 4, 5].map(star => (
              <button key={star} type="button" onClick={() => setCallQuality(callQuality === star ? 0 : star)}
                style={{ width: 32, height: 32, borderRadius: 8, border: `1px solid ${callQuality >= star ? '#f59e0b' : 'rgba(255,255,255,0.5)'}`, background: callQuality >= star ? 'rgba(245,158,11,0.15)' : 'rgba(255,255,255,0.3)', backdropFilter: 'blur(8px)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all .12s', fontSize: 16, boxShadow: callQuality >= star ? '0 0 10px rgba(245,158,11,0.35)' : 'inset 0 1px 0 rgba(255,255,255,0.7)' }}>
                {callQuality >= star ? '★' : '☆'}
              </button>
            ))}
            {callQuality > 0 && (
              <span style={{ fontSize: 11, color: '#92400e', marginLeft: 4, alignSelf: 'center', fontWeight: 600 }}>
                {['', 'Schlecht', 'Mäßig', 'Ok', 'Gut', 'Top'][callQuality]}
              </span>
            )}
          </div>
        </div>

        {/* Follow-up section – only for new calls, not edits */}
        {!isEdit && (
          <div style={{ marginBottom: 14, padding: 12, borderRadius: 10, background: createTask ? 'rgba(29,190,116,0.10)' : 'rgba(255,255,255,0.25)', border: `1px solid ${createTask ? 'var(--accent-border)' : 'rgba(255,255,255,0.50)'}`, backdropFilter: 'blur(8px)', transition: 'all .15s', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.6)' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: createTask ? 12 : 0 }}>
              <input type="checkbox" checked={createTask} onChange={e => setCreateTask(e.target.checked)}
                style={{ width: 15, height: 15, accentColor: 'var(--accent)', cursor: 'pointer' }} />
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>Follow-up Task erstellen</span>
              {!createTask && <span style={{ fontSize: 11, color: '#93a89a', marginLeft: 'auto' }}>empfohlen</span>}
            </label>

            {createTask && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'flex', gap: 8 }}>
                  <div style={{ flex: 1 }}>
                    <div style={lbStyle}>Typ</div>
                    <div style={{ display: 'flex', gap: 4 }}>
                      {FOLLOWUP_TYPES.map(t => (
                        <button key={t} onClick={() => setTaskType(t)}
                          style={{ flex: 1, height: 30, borderRadius: 6, border: `1.5px solid ${taskType === t ? '#0f8a53' : '#c8d6cb'}`, background: taskType === t ? '#effaf4' : '#fff', color: taskType === t ? '#0f8a53' : '#5e7a68', fontSize: 12, fontWeight: taskType === t ? 600 : 400, cursor: 'pointer' }}>
                          {t}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <div>
                  <div style={lbStyle}>Fälligkeit</div>
                  <div style={{ display: 'flex', gap: 4 }}>
                    {FOLLOWUP_OFFSETS.map(o => (
                      <button key={o.days} onClick={() => setTaskOffset(o.days)}
                        style={{ flex: 1, height: 30, borderRadius: 6, border: `1.5px solid ${taskOffset === o.days ? '#0f8a53' : '#c8d6cb'}`, background: taskOffset === o.days ? '#effaf4' : '#fff', color: taskOffset === o.days ? '#0f8a53' : '#5e7a68', fontSize: 11, fontWeight: taskOffset === o.days ? 600 : 400, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                        {o.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <div style={lbStyle}>Notiz (optional)</div>
                  <input type="text" value={taskNote} onChange={e => setTaskNote(e.target.value)}
                    placeholder={`z.B. Angebot besprechen nach ${selectedOutcome?.label}`}
                    style={{ ...inStyle, width: '100%', boxSizing: 'border-box' }} />
                </div>
              </div>
            )}
          </div>
        )}

        {calendarOffer && (
          <div style={{ marginBottom: 14, padding: 12, borderRadius: 8, background: 'rgba(22,163,74,0.06)', border: '1px solid rgba(22,163,74,0.25)' }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#166534', marginBottom: 8 }}>Termin im Kalender eintragen?</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              <button type="button" onClick={() => downloadIcsEvent(calendarOpts())}
                style={{ height: 32, padding: '0 12px', borderRadius: 6, border: '1px solid #86efac', background: '#fff', color: '#15803d', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                Outlook / .ics
              </button>
              <button type="button" onClick={() => window.open(buildGoogleCalendarUrl(calendarOpts()), '_blank', 'noopener,noreferrer')}
                style={{ height: 32, padding: '0 12px', borderRadius: 6, border: 'none', background: '#16a34a', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                Google Kalender
              </button>
              <button type="button" onClick={onClose}
                style={{ height: 32, padding: '0 12px', borderRadius: 6, border: '1px solid #c8d6cb', background: '#f7faf7', color: '#66796c', fontSize: 12, cursor: 'pointer', marginLeft: 'auto' }}>
                Fertig
              </button>
            </div>
          </div>
        )}

        {/* Buttons */}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          {!calendarOffer && (
            <>
              <button onClick={onClose} style={{ height: 34, padding: '0 14px', borderRadius: 6, background: '#f0f5f0', border: '1px solid #c8d6cb', color: '#5e7a68', fontSize: 13, cursor: 'pointer' }}>Abbrechen</button>
              <button onClick={handleSave} style={{ height: 34, padding: '0 18px', borderRadius: 6, background: '#159c5f', border: 'none', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', boxShadow: '0 2px 8px rgba(29,190,116,0.28)' }}>
                ✓ {isEdit ? 'Änderungen speichern' : createTask ? 'Speichern + Task' : 'Anruf speichern'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
    </>
  );
}

const lbStyle = { fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 5 };
const inStyle  = { height: 34, borderRadius: 8, border: '1px solid rgba(255,255,255,0.55)', padding: '0 10px', fontSize: 13, outline: 'none', background: 'rgba(255,255,255,0.65)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', color: 'var(--text)', fontFamily: 'var(--font)', boxSizing: 'border-box', width: '100%', transition: 'border-color .15s, box-shadow .15s', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.8)' };
